import axios, { type AxiosResponse } from "axios";
import { format } from "date-fns";
import { not, take } from "ramda";
import { EnglishLevel } from "../types/graphql";

const TITLE_CHARACTERS_LIMIT = 82;

export const noop = (): void => {};

export const truncateWithEllipses = (
  title: string,
  limit: number = TITLE_CHARACTERS_LIMIT,
): string => {
  if (title?.length > limit) {
    return `${take(limit, title)}...`;
  }
  return title;
};

// Mirrors EXTENSION_CONTENT_TYPES in api/src/common/services/gcs.service.ts so the
// PUT's Content-Type always matches the type the API signed the URL with. GCS stores
// this as the object's metadata; a real image type is required for CDN edge image
// optimization (specs/cdn-imop.md §6) to kick in.
const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  jfif: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

const contentTypeFor = (name: string): string =>
  EXTENSION_CONTENT_TYPES[name.split(".").pop()?.toLowerCase() ?? ""] ?? "application/octet-stream";

export const upload = async (url: string, file: File): Promise<AxiosResponse<any>> => {
  return await axios(url, {
    method: "PUT",
    headers: {
      "Content-Type": file.type || contentTypeFor(file.name),
    },
    data: file,
  });
};

export const staticFileUrl = (fileUrl: string | null | undefined): string | undefined => {
  if (fileUrl?.startsWith("http")) {
    return encodeURI(fileUrl);
  }
  if (fileUrl && import.meta.env.VITE_STATIC_URL) {
    return encodeURI(`${import.meta.env.VITE_STATIC_URL}/${fileUrl}`);
  }
  return undefined;
};

/** Canonical CDN image-optimization width variants (specs/cdn-imop.md §5). */
const CANONICAL_IMAGE_WIDTHS = [48, 64, 96, 128, 160, 224, 640, 960, 1280];

/** Extensions Cloud CDN can transform — never svg/gif/documents (§3.1). */
const TRANSFORMABLE_IMAGE_PATTERN = /\.(jpe?g|png|webp|bmp|tiff?|avif)$/i;

/**
 * Admin CDN image-optimization URL builder (specs/cdn-imop.md §4.5).
 * Mirrors web/packages/shared/helpers/image.ts in a small, independent form:
 * - inert unless `VITE_IMAGE_OPTIMIZATION === "true"` (kill switch);
 * - absolute foreign URLs and non-transformable files pass through untouched;
 * - the requested width snaps to the canonical variant set so admin traffic
 *   cannot fragment the CDN edge cache (§5).
 */
export const adminImageUrl = (
  fileUrl: string | null | undefined,
  { width }: { width: number },
): string | undefined => {
  if (!fileUrl || import.meta.env.VITE_IMAGE_OPTIMIZATION !== "true") {
    return staticFileUrl(fileUrl);
  }

  let bucketPath = fileUrl;
  if (fileUrl.startsWith("http")) {
    try {
      const staticUrl = import.meta.env.VITE_STATIC_URL;
      if (!staticUrl) {
        return staticFileUrl(fileUrl);
      }
      const file = new URL(fileUrl);
      const base = new URL(staticUrl);
      const basePath = base.pathname.replace(/\/+$/, "");
      if (
        file.host.toLowerCase() !== base.host.toLowerCase() ||
        !file.pathname.startsWith(`${basePath}/`)
      ) {
        // Different origin — never transform foreign URLs.
        return staticFileUrl(fileUrl);
      }
      bucketPath = file.pathname.slice(basePath.length + 1);
    } catch {
      return staticFileUrl(fileUrl);
    }
  }

  const baseUrl = staticFileUrl(bucketPath);
  if (!baseUrl || !TRANSFORMABLE_IMAGE_PATTERN.test(bucketPath)) {
    return baseUrl;
  }

  // Nearest canonical width (ties round up) keeps the edge variant count bounded.
  const canonicalWidth = CANONICAL_IMAGE_WIDTHS.reduce((best, candidate) =>
    Math.abs(candidate - width) <= Math.abs(best - width) ? candidate : best,
  );
  return `${baseUrl}?imop=Resize,width=${canonicalWidth};Compress,strength=medium;Convert,format=webp`;
};

export const isThirdPartyTesting = () => {
  return import.meta.env.VITE_THIRD_PARTY_TESTING === "true";
};

export const getUTCOffset = (timezone: string | null | undefined): string => {
  if (!timezone) return "";
  try {
    const etcGmtMatch = timezone.match(/^Etc\/GMT([+-])(\d+)$/);
    if (etcGmtMatch) {
      const sign = etcGmtMatch[1] === "+" ? "-" : "+";
      return `UTC${sign}${etcGmtMatch[2]}`;
    }
    if (timezone === "Etc/GMT" || timezone === "UTC" || timezone === "GMT") {
      return "UTC+0";
    }
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());
    const offset = parts.find((p) => p.type === "timeZoneName")?.value ?? "";
    if (offset === "GMT") return "UTC+0";
    return offset.replace("GMT", "UTC");
  } catch {
    return "";
  }
};

export const transformUsersDataToCSV = (responseData: any) => {
  const ALL_EMAILS = "Users Emails";
  const DATE_COLUMNS = [
    "createdAt",
    "birthday",
    "lastOnlineAt",
    "pausedUntil",
    "currentPeriodStart",
    "currentPeriodEnd",
    "resumedAt",
    "cancelledAt",
  ];

  const SUBSCRIPTION_KEYS = [
    "status",
    "plan",
    "currentPeriodStart",
    "currentPeriodEnd",
    "resumesAt",
    "cancelledAt",
  ];
  const EXCLUDED_KEYS = [
    "customers",
    "pauseCredits",
    "showBirthday",
    "completedPercentage",
    "isVisible",
    "wantsNewsletter",
    "isActive",
  ];

  const RENAMED_COLUMN_IS_ACTIVE = "Confirmed Email";
  const TIMEZONE_UTC_COLUMN = "timezoneUTC";
  const mainUserProperties = Object.keys(responseData[0]).filter((columnName) =>
    not(EXCLUDED_KEYS.includes(columnName)),
  );
  const timezoneIndex = mainUserProperties.indexOf("timezone");
  if (timezoneIndex !== -1) {
    mainUserProperties.splice(timezoneIndex + 1, 0, TIMEZONE_UTC_COLUMN);
  }

  const secondaryUserProperties = Object.keys(responseData[0]).filter(
    (columnName) => EXCLUDED_KEYS.includes(columnName) && columnName !== "customers",
  );
  const columnNames = [
    ...mainUserProperties,
    RENAMED_COLUMN_IS_ACTIVE,
    ...SUBSCRIPTION_KEYS,
    ...secondaryUserProperties,
    ALL_EMAILS,
  ];

  let emails: string[] = [];
  responseData.forEach((_item: any) => {});
  const rows = responseData.map((item: any, index: number) =>
    columnNames.map((columnName) => {
      const value = item[columnName];
      if (columnName === "email") {
        emails = [...emails, value];
      }
      if (columnName === TIMEZONE_UTC_COLUMN) {
        return getUTCOffset(item.timezone);
      }
      if (columnName === RENAMED_COLUMN_IS_ACTIVE) {
        return item.isActive ? "Yes" : "No";
      }
      if (SUBSCRIPTION_KEYS.includes(columnName)) {
        const subscriptionFieldValues = item.customers[0]?.subscriptions
          ?.map((subscription: any) => subscription[columnName])
          .filter((value: number | null) => value != null);
        if (DATE_COLUMNS.includes(columnName) && subscriptionFieldValues) {
          return subscriptionFieldValues
            .map((value: number) => format(value, "yyyy-MM-dd"))
            .join(";");
        }
        if (columnName === "plan") {
          return subscriptionFieldValues?.map((value: any) => value?.item_price_id).join(";");
        }
        return subscriptionFieldValues?.join(";");
      }
      if (columnName === "cambridgeEnglishLevel" && value) {
        const EnglishLevels = Object.values(EnglishLevel);
        return EnglishLevels[value];
      }
      if (columnName === "nativeLanguage") {
        return value?.replaceAll(",", " ");
      }
      if (Array.isArray(value)) {
        const serializedArray = value.map((arrayItem) =>
          JSON.stringify(arrayItem).replace(",", " "),
        );
        return serializedArray.join("; ");
      }
      if (DATE_COLUMNS.includes(columnName) && value) {
        return format(value, "yyyy-MM-dd");
      }
      if (columnName === "occupation" && value) {
        return value.name;
      }
      if (columnName === ALL_EMAILS) {
        return index === responseData.length - 1 ? `"${emails.join(",")}"` : null;
      }
      return value;
    }),
  );

  return [columnNames, ...rows].map((row) => row.join(",")).join("\n");
};

/**
 * Debounce function was removed in Material UI v5.
 * The function below is provided the by Material UI documentation:
 * https://mui.com/x/react-date-pickers/lifecycle/#system-ServerInteraction.tsx
 */
export const debounce = (func: (...arg: any) => void, wait = 500) => {
  let timeout: NodeJS.Timeout;
  function debounced(...args: any) {
    const later = () => {
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  }

  debounced.clear = () => {
    clearTimeout(timeout);
  };

  return debounced;
};
